import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export const commandMap = [
  // 🏠 GENERAL
  { keywords: ["home", "dashboard"], route: "/home" },
  { keywords: ["settings"], route: "/settings" },

  // 🎓 STUDENTS
  {
    keywords: ["student details", "all students"],
    route: "/student-details",
  },
  {
    keywords: [
      "add student",
      "new admission",
      "take admission",
      "enroll student",
    ],
    route: "/add-student",
  },
  { keywords: ["left students"], route: "/left-students" },
  { keywords: ["inactive students"], route: "/inactive-students" },
  { keywords: ["scholarship"], route: "/scholarship" },

  // 👨‍🏫 HR
  { keywords: ["staff directory", "all staff"], route: "/staff-directory" },
  { keywords: ["add staff"], route: "/add-staff" },
  { keywords: ["staff attendance"], route: "/staff-attendance" },
  {
    keywords: ["staff leave", "staff applied leave"],
    route: "/staff-applied-leave",
  },
  { keywords: ["leave request"], route: "/leave-request" },
  { keywords: ["left staff"], route: "/left-staff" },
  { keywords: ["teachers rating"], route: "/teachers-rating" },

  // 💰 FEES
  { keywords: ["collect fees"], route: "/collect-fees" },
  { keywords: ["fees master"], route: "/fees-master" },
  { keywords: ["monthly fees"], route: "/monthly-fees" },
  {
    keywords: ["dues fee", "pending fees", "defaulters"],
    route: "/dues-fee",
  },
  { keywords: ["paid fees"], route: "/paid-fee" },
  { keywords: ["admission form"], route: "/admission-form" },
  { keywords: ["sell admission form"], route: "/sell-admission-form" },
  { keywords: ["miscellaneous fees"], route: "/miscellaneous-fees" },
  { keywords: ["collection summary"], route: "/collectedby-summary" },
  { keywords: ["payment status"], route: "/check-payment-status" },

  // 🚌 TRANSPORT
  { keywords: ["bus details"], route: "/bus-details" },
  { keywords: ["add bus"], route: "/add-bus" },
  { keywords: ["track bus"], route: "/track-bus" },
  { keywords: ["driver details"], route: "/driver-details" },
  { keywords: ["bus stoppage"], route: "/bus-stoppage" },
  { keywords: ["bus route"], route: "/bus-route" },
  { keywords: ["bus timing"], route: "/bus-timing" },
  { keywords: ["student transport"], route: "/student-transport-details" },

  // 🏢 FRONT OFFICE
  { keywords: ["admission enquiry"], route: "/admission-enquiry" },
  { keywords: ["visitor book"], route: "/visitor-book" },
  { keywords: ["complain box", "complaints"], route: "/complain-box" },
  { keywords: ["gate pass"], route: "/gate-pass" },
  { keywords: ["medical alert"], route: "/medical-alert" },
  { keywords: ["student front details"], route: "/view-student-details" },

  // ⚠️ BEHAVIOUR
  { keywords: ["incidents"], route: "/incidents" },
  { keywords: ["assign incidents"], route: "/assign-view-incidents" },

  // 📢 COMMUNICATION
  { keywords: ["notice board"], route: "/notice-board" },
  { keywords: ["send email"], route: "/send-email" },
  { keywords: ["send sms"], route: "/send-sms" },
  { keywords: ["send notification"], route: "/send-push-notification" },
  { keywords: ["dfs message"], route: "/send-dfs-message" },
  { keywords: ["sms template"], route: "/sms-template" },
  { keywords: ["holidays"], route: "/holidays" },

  // 🧾 EXPENSE
  { keywords: ["expenses"], route: "/expense-details" },

  // 👨‍💻 ADMIN
  { keywords: ["user details"], route: "/user-details" },
  { keywords: ["live event"], route: "/live-event" },

  // 📚 HOMEWORK
  { keywords: ["homework"], route: "/homework" },
  { keywords: ["homework statistics"], route: "/homework-statistics" },

  // 📊 ATTENDANCE
  { keywords: ["school attendance"], route: "/school-attendance" },
  { keywords: ["bus attendance"], route: "/bus-attendance" },
  { keywords: ["bus attendance stats"], route: "/bus-attendance-statistics" },
  { keywords: ["student attendance"], route: "/student-attendance" },
  { keywords: ["class attendance"], route: "/class-section-attendance" },
  { keywords: ["applied leave"], route: "/applied-leave" },
  { keywords: ["bus wise attendance"], route: "/bus-wise-attendance" },
  {
    keywords: ["school attendance stats"],
    route: "/school-attendance-statistics",
  },

  // 📖 LIBRARY
  { keywords: ["book list"], route: "/book-list" },
  { keywords: ["add book"], route: "/add-book" },
  { keywords: ["ebook list"], route: "/e-book-list" },
  {
    keywords: ["issue book", "return book"],
    route: "/book-issue-return-student",
  },

  // 📝 EXAMS
  { keywords: ["syllabus"], route: "/syllabus" },
  { keywords: ["sample paper"], route: "/sample-paper" },
  { keywords: ["admit card"], route: "/admit-card" },
  { keywords: ["exam marks"], route: "/examination-marks" },
  { keywords: ["add exam"], route: "/add-examination-details" },
  { keywords: ["result"], route: "/result" },
  { keywords: ["toppers"], route: "/toppers" },
  {
    keywords: ["go back", "previous page", "back"],
    route: -1, // special case
  },
];

const VoiceNavigator = () => {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const navigate = useNavigate();

  const normalizeText = (text) => {
    return text
      .toLowerCase()

      .replace(/please|show|open|go to|me|the|all|list|of/gi, "")
      .replace(/\b(students|student)\b/g, "student")
      .replace(/\b(teachers|teacher)\b/g, "teacher")
      .replace(/\b(staffs|staff)\b/g, "staff")
      .replace(/\b(buses|bus)\b/g, "bus")
      .replace(/\b(books|book)\b/g, "book")
      .replace(/\b(fees|fee)\b/g, "fees")

      .replace(/\s+/g, " ")
      .trim();
  };

  // ✅ Handle Command
  const handleCommand = (text) => {
    const cleaned = normalizeText(text);

    const match = commandMap.find((cmd) =>
      cmd.keywords.some((keyword) => {
        const normalizedKeyword = normalizeText(keyword);

        return normalizedKeyword
          .split(" ")
          .every((word) => cleaned.includes(word));
      }),
    );

    if (match) {
      navigate(match.route);
    }
  };

  // ✅ Start Listening
  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      toast.error("Speech Recognition Not Supported");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.continuous = false;

    setListening(true);
    recognition.start();

    recognition.onresult = (event) => {
      const text = event.results[0][0].transcript;
      setTranscript(text);
      setListening(false);
      handleCommand(text);
    };

    recognition.onerror = () => {
      setListening(false);
    };
  };

  return (
    <div>
      <button
        onClick={startListening}
        class="btn btn-light btn-icon rounded-circle d-flex align-items-center"
        type="button"
      >
        {listening ? <i class="bi bi-mic-fill"></i> : <i class="bi bi-mic"></i>}
      </button>
    </div>
  );
};

export default VoiceNavigator;
